import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { parseUtcTimestamp } from 'src/utils/timestamps';

interface TimeseriesData {
  timestamps: string[];
  values: number[];
  values_dev?: (number | null)[];
}

interface TemporalPlotProps {
  timeseries: Record<string, TimeseriesData>;
  selectedTimestamp: string | null;
  onTimestampSelect: (timestamp: string) => void;
  // 'voltage' plots raw values; 'deviation' plots per-contact value_z.
  mode?: 'voltage' | 'deviation';
}

const MARGIN = { top: 20, right: 20, bottom: 50, left: 50 };
const COLORS = d3.schemeTableau10;

// Which per-series array to plot, by mode.
const seriesValues = (
  d: TimeseriesData,
  mode: NonNullable<TemporalPlotProps['mode']>
): (number | null)[] => (mode === 'deviation' ? (d.values_dev ?? []) : d.values);

const xDomain = (frames: { date: Date }[]): [Date, Date] => [
  frames[0].date,
  frames[frames.length - 1].date,
];

const TemporalPlot = ({
  timeseries,
  selectedTimestamp,
  onTimestampSelect,
  mode = 'voltage',
}: TemporalPlotProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Parse all timestamps once
  const electrodes = useMemo(() => Object.keys(timeseries), [timeseries]);

  // The frames with a value in this mode, oldest first. Lines, axis, marker and
  // click all position through these dates so they cannot drift apart.
  const frames = useMemo(() => {
    const byTimestamp = new Map<string, Date>();
    for (const data of Object.values(timeseries)) {
      const vals = seriesValues(data, mode);
      data.timestamps.forEach((ts, i) => {
        const v = vals[i];
        if (v == null || Number.isNaN(v) || byTimestamp.has(ts)) return;
        const date = parseUtcTimestamp(ts);
        if (date) byTimestamp.set(ts, date);
      });
    }
    return [...byTimestamp]
      .map(([timestamp, date]) => ({ timestamp, date }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [timeseries, mode]);

  // Stable resize handler — only updates dimensions state
  const measureContainer = useCallback(() => {
    if (!containerRef.current) return;
    const { clientWidth, clientHeight } = containerRef.current;
    setDimensions((prev) => {
      if (prev.width === clientWidth && prev.height === clientHeight) return prev;
      return { width: clientWidth, height: clientHeight };
    });
  }, []);

  // Measure on mount + resize
  useEffect(() => {
    measureContainer();
    if (!containerRef.current) return;
    const observer = new ResizeObserver(measureContainer);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [measureContainer]);

  // Draw chart — depends on dimensions (stable) and data, NOT selectedTimestamp
  useEffect(() => {
    if (!svgRef.current || electrodes.length === 0) return;
    const { width, height } = dimensions;
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = height - MARGIN.top - MARGIN.bottom;
    if (innerW <= 0 || innerH <= 0 || frames.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const yLabel = mode === 'deviation' ? 'Deviation (z)' : 'Voltage (V)';

    // Scales
    const allValues: number[] = [];
    for (const data of Object.values(timeseries)) {
      for (const v of seriesValues(data, mode)) {
        if (v != null && !Number.isNaN(v)) allValues.push(v);
      }
    }

    const xExtent = xDomain(frames);
    const yExtent = d3.extent(allValues) as [number, number];
    const yPad = (yExtent[1] - yExtent[0]) * 0.05 || 1;

    const xScale = d3.scaleTime().domain(xExtent).range([0, innerW]);
    const yScale = d3
      .scaleLinear()
      .domain([yExtent[0] - yPad, yExtent[1] + yPad])
      .range([innerH, 0]);

    // X-axis with date + time format
    const timeSpanMs = xExtent[1].getTime() - xExtent[0].getTime();
    const timeSpanHours = timeSpanMs / (1000 * 60 * 60);
    const xFormat =
      timeSpanHours <= 24 ? d3.timeFormat('%H:%M\n%d %b') : d3.timeFormat('%d %b\n%H:%M');

    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(6)
          .tickFormat(xFormat as any)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('text-anchor', 'middle');

    g.append('g').call(d3.axisLeft(yScale).ticks(6)).selectAll('text').attr('font-size', '11px');

    // Y-axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -MARGIN.left + 14)
      .attr('x', -innerH / 2)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('fill', 'var(--color-text-secondary, #888)')
      .text(yLabel);

    // Lines per electrode (skip null points so gaps don't draw to 0)
    const line = d3
      .line<[Date, number]>()
      .defined((d) => d[1] != null && !Number.isNaN(d[1]))
      .x((d) => xScale(d[0]))
      .y((d) => yScale(d[1]));

    electrodes.forEach((electrode, idx) => {
      const data = timeseries[electrode];
      const vals = seriesValues(data, mode);
      const points: [Date, number][] = data.timestamps.map((ts, i) => [
        parseUtcTimestamp(ts)!,
        vals[i] as number,
      ]);

      g.append('path')
        .datum(points)
        .attr('fill', 'none')
        .attr('stroke', COLORS[idx % COLORS.length])
        .attr('stroke-width', 1.5)
        .attr('d', line);
    });

    // Click handler overlay — stores scales in closure for the marker effect
    g.append('rect')
      .attr('class', 'click-overlay')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .attr('cursor', 'crosshair')
      .on('click', (event: MouseEvent) => {
        const [mx] = d3.pointer(event);
        const clickedDate = xScale.invert(mx);

        let bestTs = frames[0].timestamp;
        let bestDiff = Infinity;
        for (const { timestamp, date } of frames) {
          const diff = Math.abs(date.getTime() - clickedDate.getTime());
          if (diff < bestDiff) {
            bestDiff = diff;
            bestTs = timestamp;
          }
        }
        onTimestampSelect(bestTs);
      });
  }, [timeseries, electrodes, dimensions, frames, onTimestampSelect, mode]);

  // Separate effect for the selected timestamp marker — avoids full redraw
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);

    // Remove old marker
    svg.selectAll('.timestamp-marker').remove();

    if (!selectedTimestamp) return;

    const { width, height } = dimensions;
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = height - MARGIN.top - MARGIN.bottom;
    if (innerW <= 0 || innerH <= 0 || frames.length === 0) return;

    // Reconstruct xScale (lightweight, no DOM mutation)
    const xScale = d3.scaleTime().domain(xDomain(frames)).range([0, innerW]);

    const selectedDate = parseUtcTimestamp(selectedTimestamp);
    if (!selectedDate) return;
    const xPos = xScale(selectedDate);
    if (xPos >= 0 && xPos <= innerW) {
      svg
        .select('g')
        .append('line')
        .attr('class', 'timestamp-marker')
        .attr('x1', xPos)
        .attr('x2', xPos)
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', 'var(--color-error, #e94560)')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '6,3');
    }
    // Depend on the same inputs as the chart-draw effect (electrodes,
    // frames) so that whenever the chart re-runs its selectAll('*').remove(),
    // this effect re-runs right after and re-adds the marker. Otherwise a chart
    // redraw triggered by an unrelated re-render would silently wipe the line.
  }, [selectedTimestamp, dimensions, timeseries, electrodes, frames]);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}
    >
      <svg
        ref={svgRef}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      />
    </div>
  );
};

export default TemporalPlot;
